import { observer } from 'mobx-react-lite';
import { styled } from 'styled-components';
import { FilteredSearchResultsView } from './FilteredSearchResultsView';
import { SingleOriginFilterView } from './SingleOriginFilterView';
import { RecordingViewState } from './RecordingViewState';
import { RecordingSearchState } from "./RecordingSearchState";
import { PageHeaderElement, RecordingPageHeaderView, SearchTabsView } from './HeaderView';
import { AllOption } from './SingleOriginFilterState';
import { BorderlessButton, BorderlessCancelButton, ReportContainer } from './common-styles';
import { DomainReportView } from './Reports/DomainReportView';
import { SearchReportByDestinationView } from './Reports/SearchReportByDestinationView';
import { SearchReportBySearchTermView } from './Reports/SearchReportBySearchTermView';
import { DomainInstrumentationReport } from './Reports/DomainInstrumentationReport';
import { toJS } from 'mobx';
import { htmlEncode } from '../../lib/utils/htmlEncode';
import { ProcessInfo } from '../../State/ProcessInfo';
import { PrettyPrintedJsonObjectView } from './PrettyPrintedJsonObjectView';
import { TrackingSummaryReportView } from './Reports/TrackingSummaryReportView';


const ControlColumn = styled.div`
	box-sizing: border-box;
	width: 100vw;
	display: flex;
	flex-direction: column;
	align-items: center;
`

const FloatingControlColumn = styled(ControlColumn)`
	position: fixed;
	background-color: rgba(255, 255, 255, 0.9);
`;

export const ControlRowContainer = styled.div`
	padding-top: 2px;
	padding-bottom: 2px;
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	column-gap: 2rem;
	& label {
		font-family: serif;
	}
`;

const ControlWithLabelOnTopContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	& label {
		font-family: serif;
		padding-top: 0.1rem;
		padding-right:	0.5rem;
		padding-left: 0.5rem;
	}
`;


const RecordingViewPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	align-items: center;
`;

//.attrs({rows: 5, cols: 24})`
const SearchTermTextArea = styled.textarea`
	width: calc(min(25vw, 10rem));
	height: 4rem;
	font-size: 75%;
`;

const SuggestedSearchTermContainer = styled.div`
	width: calc(min(25vw, 10rem));
	height: 4rem;
	font-size: 75%;
	border: 1px solid black;
	display: flex;
	flex-direction: column;
	justify-content: start;
	align-items: stretch;
	overflow-x: auto;
`;
const SuggestedSearchTermRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: start;
	max-width: 15vw;
	gap: 0.25rem;
`;

const SearchTermsControlsVerticalSpacer = styled.div`
	height: 6.5rem;
`;
const SearchTermsAndFiltersControlVerticalSpacer = styled.div`
	height: 12.5rem;
`;

export const OverlayContainerElement = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1000;
	background-color: rgba(255, 255, 255, 0.875);
	box-sizing: border-box;
	width: 100vw;
	height: 100vh;
	padding: 10vh 10vw;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`

export const OverlayInsertElement = styled.div`
	display: block;
	position: relative;
	background-color: rgba(255, 255, 255, 1);
	font-family: monospace;
	box-sizing: border-box;
	max-width: 80vw;
	max-height: 80vh;
	overflow-x: scroll;
	overflow-y: scroll;
`

const OverlayHeaderRow = styled.div`
	background-color: rgba(255, 255, 255, 0.975);
	position: fixed;
	z-index: 1001;
	left: 10vw;
	height: 1.5rem;
	top: calc(10vh - 1.5rem);
	width: 80vw;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: baseline;
`

export const JsonOverlayView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	const {objectToDisplayAsJson} = recordingViewState;
	if (objectToDisplayAsJson == null) return null;
	return (
		<OverlayContainerElement>
			<OverlayInsertElement>
				<OverlayHeaderRow>					
					<BorderlessButton onClick={() => {
						navigator.clipboard.writeText(
							`\`\`\`json\n${JSON.stringify(objectToDisplayAsJson,null,'\t')}\n\`\`\``)}}>📋 copy</BorderlessButton>
					<BorderlessButton onClick={() => {recordingViewState.setObjectToDisplayAsJson(undefined)}}>✖</BorderlessButton>
				</OverlayHeaderRow>
				<PrettyPrintedJsonObjectView objectToPrettyPrintAsJson={objectToDisplayAsJson} />
			</OverlayInsertElement>
		</OverlayContainerElement>
	)
});


const SelectSingleSearchTermControl = observer ( ( ({recordingSearchState}: {recordingSearchState: RecordingSearchState}) => {
	if (recordingSearchState.searchTerms.length <= 1) return null;
	return (<>
		<ControlRowContainer>
			<label>Show only search term:&nbsp;
				<select value={recordingSearchState.showOnlySearchTerm}
					onChange={ (e) => recordingSearchState.setShowOnlySearchTerm(e.currentTarget.value) }
				>
					<option value="">All</option>
					{ recordingSearchState.searchTerms.map( (searchTerm) => (
						<option key={searchTerm} value={searchTerm}>{searchTerm}</option>
					))}
				</select>
			</label>
			<label>Data flow&nbsp;
				<select value={recordingSearchState.dataFlowDirection}
					onChange={ (e) => recordingSearchState.setDataFlowDirection(e.currentTarget.value) }>
					<option value={AllOption}>All</option>
					<option value="sent">Sent</option>
					<option value="received">Received</option>
				</select>
			</label>
		</ControlRowContainer>
		</>
	);
}));


export const SearchTermControlsView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	const {searchState: recordingSearchState} = recordingViewState;
	const newDomGeneratedSearchTermsToIgnore = (searchTerm: string) => ({
		domGeneratedSearchTermsToIgnore: [
			...(recordingSearchState.dbRecordingEntryWriter?.recording.domGeneratedSearchTermsToIgnore ?? []),
			searchTerm
		]
	})
	const removeSuggestedSearchTerm = async (searchTerm: string) => {
		await recordingSearchState.dbRecordingEntryWriter?.updateRecording(newDomGeneratedSearchTermsToIgnore(searchTerm));
	}
	const ignoreSuggestedSearchTerm = (searchTerm: string) => () => removeSuggestedSearchTerm(searchTerm);
	const acceptSuggestedSearchTerm = (searchTerm: string) => async () => {
		removeSuggestedSearchTerm(searchTerm);
		await recordingSearchState.dbRecordingEntryWriter?.updateRecording({
			...newDomGeneratedSearchTermsToIgnore(searchTerm),
			searchTermsNewlineSeparated: [...new Set<string>([
				...(recordingSearchState.dbRecordingEntryWriter?.recording.searchTermsNewlineSeparated ?? "").split("\n")
					.map(s => s.trim())
					.filter(s => s.length > 0),
				searchTerm
			])].sort().join("\n")
		});
	}

	return (
			<FloatingControlColumn>
				<ControlWithLabelOnTopContainer>
					<label>Search Terms</label>
					<ControlRowContainer>
						<ControlWithLabelOnTopContainer>
							<label>Inferred</label>
							<SuggestedSearchTermContainer>{
								recordingSearchState.searchTermsFromDomEvents.map( (searchTerm) => (
									<SuggestedSearchTermRow key={searchTerm}>
										<BorderlessCancelButton onClick={ignoreSuggestedSearchTerm(searchTerm)}>✗</BorderlessCancelButton>
										<BorderlessButton onClick={acceptSuggestedSearchTerm(searchTerm)}>✓</BorderlessButton>
										{searchTerm}
									</SuggestedSearchTermRow>
							))}
							</SuggestedSearchTermContainer>
						</ControlWithLabelOnTopContainer>
						<ControlWithLabelOnTopContainer>
							<label>For this recording</label>
							<SearchTermTextArea
								defaultValue={recordingSearchState.searchTermTextAreaValue}
								onChange={ (e) => recordingSearchState.setSearchTermTextAreaValue(e.currentTarget.value) } />
						</ControlWithLabelOnTopContainer>
						<ControlWithLabelOnTopContainer>
							<label>For all recordings</label>
							<SearchTermTextArea
								defaultValue={recordingSearchState.GlobalSearchTermTextAreaValue}
								onChange={ (e) => { recordingSearchState.setGlobalSearchTermAreaValue(e.currentTarget.value); } } />
						</ControlWithLabelOnTopContainer>
					</ControlRowContainer>
					{ recordingViewState.searchTab !== 'chronological' ? null : (<>
						<SelectSingleSearchTermControl {...{recordingSearchState}} />
						<SingleOriginFilterView label={"Page"} singleOriginFilterState={recordingSearchState.sourcePageUrlFilterState} />
						<SingleOriginFilterView label={"Destination"} singleOriginFilterState={recordingSearchState.destinationUrlFilterState}/>						
						</>)}					
				</ControlWithLabelOnTopContainer>
			</FloatingControlColumn>
	)
});


export const ChronologicalSearchFilterSpacer = styled.div`
	height: 5rem;
`;

export const RecordingSearchView = observer ( ({recordingSearchState}: {recordingSearchState: RecordingSearchState}) => {
	return (<>
			<FilteredSearchResultsView recordingSearchState={recordingSearchState} />
		</>
	);
});


export const SearchReportRouterView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	switch (recordingViewState.searchTab) {
		case 'chronological': return <RecordingSearchView recordingSearchState={recordingViewState.searchState} />;
		case 'by search term': return <SearchReportBySearchTermView {...{recordingViewState}} />;
		case 'by destination': return <SearchReportByDestinationView {...{recordingViewState}} />;
		default: return null;
	}
});

export const SearchReportsWithControlsView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	return (<>
		<SearchTermControlsView {...{recordingViewState}} />
		<RecordingViewPage>
			{(recordingViewState.searchTab === 'chronological') ?
				<SearchTermsAndFiltersControlVerticalSpacer/> :
				<SearchTermsControlsVerticalSpacer/>
			}
			<ReportContainer>
				<SearchReportRouterView {...{recordingViewState}} />
			</ReportContainer>
		</RecordingViewPage>
	</>);
});


export const DomainReportsRouterView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	switch (recordingViewState.reportTab) {
		case 'tracking':
			return <TrackingSummaryReportView trackingSummaryReportState={recordingViewState.trackingSummaryReportState} displayJson={recordingViewState.setObjectToDisplayAsJson} />;
		case 'instrumentation':
			return <DomainInstrumentationReport instrumentationReportingState={recordingViewState.instrumentationReportingState} />;
		default: return null;
	}
});


export const ReportRouterView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	switch (recordingViewState._topTab) {
		case 'search':
			return <SearchReportRouterView {...{recordingViewState}} />;
		case 'reports':
			return <DomainReportsRouterView {...{recordingViewState}} />;
		default: return null;
	}
});


export const VerticalSpacerSearchTabs = styled.div`
	height: 1.5rem;
`;
export const SearchReportHeaderView = observer( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	return (<>
		<PageHeaderElement>
			<SearchTabsView recordingViewState={recordingViewState} />
		</PageHeaderElement>
		<VerticalSpacerSearchTabs />
		</>)
});

export const StandAloneSearchReportView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	return (<>
		<SearchReportHeaderView recordingViewState={recordingViewState} />
		<SearchReportsWithControlsView recordingViewState={recordingViewState} />
	</>)
});

export const RecordingView = observer ( ({recordingViewState}: {recordingViewState: RecordingViewState}) => {
	return (<>
		<RecordingPageHeaderView {...{recordingViewState}} />
		<RecordingViewPage>{
			recordingViewState.topTab === 'search' ? (
				<SearchReportsWithControlsView {...{recordingViewState}} />
			) : recordingViewState.topTab === 'reports' ? (
				<ReportContainer>
					<DomainReportsRouterView {...{recordingViewState}} />
				</ReportContainer>
				) : null 
			}
			{ recordingViewState.objectToDisplayAsJson == null ? null : (
				<JsonOverlayView {...{recordingViewState}} />
			)}
		</RecordingViewPage>
	</>);
});
